import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Video, ChevronLeft, Settings2, Plus, Pencil, Trash2, ExternalLink, X, Check, Play, Loader2,
} from 'lucide-react';

const emptyForm = { id: null, name: '', url: '', tenant_visible: false };

// A stored camera URL that points at a direct video file is played with a
// native HTML5 <video> (mp4/webm/ogg/mov). Anything else keeps the existing
// iframe embed behaviour (real CCTV embed pages) — no regression.
const isVideoUrl = (url) => /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test((url || '').trim());

// Native video feed: muted+loop+autoplay+playsInline so a pre-recorded demo
// clip behaves like a continuous LIVE feed. If the browser blocks autoplay we
// surface a visible Play control instead of failing silently.
function CameraVideo({ cam, t }) {
    const ref = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [needsPlay, setNeedsPlay] = useState(false);

    const attemptPlay = useCallback(() => {
        const v = ref.current;
        if (!v) return;
        // iOS inline muted autoplay contract: must be muted (property + attr) and
        // playsinline. Re-assert every attempt (React's `muted` attr is unreliable).
        v.muted = true;
        v.defaultMuted = true;
        v.setAttribute('muted', '');
        const p = v.play();
        if (p && typeof p.then === 'function') p.catch(() => { /* blocked check handles UX */ });
    }, []);

    useEffect(() => {
        setLoading(true); setError(false); setNeedsPlay(false);
        const v = ref.current;
        if (!v) return;
        v.muted = true;
        v.defaultMuted = true;
        v.loop = true;
        v.playsInline = true;
        v.setAttribute('muted', '');
        v.setAttribute('playsinline', '');
        v.load();
        attemptPlay();
        const t1 = setTimeout(attemptPlay, 500);
        // Only surface a Play control if autoplay is GENUINELY blocked (still
        // paused after the video is playable) — avoids a false Play button flash
        // while muted autoplay is still starting.
        const blockedCheck = setTimeout(() => {
            const el = ref.current;
            if (el && el.paused && el.readyState >= 2) setNeedsPlay(true);
        }, 2200);
        return () => { clearTimeout(t1); clearTimeout(blockedCheck); };
    }, [cam.id, cam.url, attemptPlay]);

    const manualPlay = () => {
        const v = ref.current;
        if (!v) return;
        v.muted = true;
        v.play().then(() => setNeedsPlay(false)).catch(() => setNeedsPlay(true));
    };

    // Belt-and-suspenders loop: if `loop` is ever ignored, restart on end so the
    // feed never shows a stopped/"ended" state.
    const handleEnded = () => {
        const v = ref.current;
        if (!v) return;
        try { v.currentTime = 0; v.play().catch(() => {}); } catch (_) { /* noop */ }
    };

    return (
        <div className="absolute inset-0 w-full h-full">
            <video ref={ref} key={cam.id} data-testid="cameras-video"
                className="absolute inset-0 w-full h-full object-contain bg-black"
                autoPlay loop muted playsInline preload="auto"
                controls={needsPlay}
                onLoadedMetadata={() => { setLoading(false); attemptPlay(); }}
                onLoadedData={() => { setLoading(false); setError(false); attemptPlay(); }}
                onCanPlay={() => { setLoading(false); setError(false); attemptPlay(); }}
                onPlaying={() => { setLoading(false); setError(false); setNeedsPlay(false); }}
                onEnded={handleEnded}
                onError={() => { const v = ref.current; if (!v || v.readyState < 2) { setError(true); setLoading(false); } }}>
                {/* MP4 is the primary, direct source. A WebM sibling (when present)
                    guarantees playback on browsers that lack H.264. */}
                <source src={cam.url} type="video/mp4" />
                {/\.mp4(\?.*)?$/i.test(cam.url) && (
                    <source src={cam.url.replace(/\.mp4(\?.*)?$/i, '.webm')} type="video/webm" />
                )}
            </video>

            {!error && (
                <span className="absolute top-3 left-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-white bg-red-600/90 px-2 py-0.5 rounded" data-testid="cameras-live-badge">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />{t('commandCenter.cameras.liveBadge')}
                </span>
            )}

            {loading && !error && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40" data-testid="cameras-video-loading">
                    <div className="flex items-center gap-2 text-slate-300 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" />{t('commandCenter.cameras.videoLoading')}
                    </div>
                </div>
            )}

            {needsPlay && !error && (
                <button type="button" onClick={manualPlay} data-testid="cameras-video-play"
                    className="absolute inset-0 flex items-center justify-center bg-black/50 hover:bg-black/40 transition-colors">
                    <span className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-semibold px-5 py-3 rounded-full">
                        <Play className="w-5 h-5" />{t('commandCenter.cameras.playFeed')}
                    </span>
                </button>
            )}

            {error && (
                <div className="absolute inset-0 flex items-center justify-center text-center px-6" data-testid="cameras-video-error">
                    <p className="text-slate-400 text-sm">{t('commandCenter.cameras.videoError')}</p>
                </div>
            )}
        </div>
    );
}

export default function CamerasPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const org = params.get('org') || undefined;
    // Opened from an automotive lot entry point: the empty state names the lot
    // instead of using generic wording. Never applied to other verticals.
    const lotScope = params.get('scope') === 'lot';
    // Back goes where the viewer came from. Opened from the Sales Desk it
    // returns there (a salesperson cannot load the Command Center); every other
    // entry point keeps its existing Command Center destination.
    const fromSalesDesk = params.get('from') === 'sales-desk';
    const backTo = fromSalesDesk ? '/sales-desk' : `/command-center${org ? `?org=${org}` : ''}`;
    const backLabel = fromSalesDesk
        ? t('commandCenter.cameras.backToSalesDesk', 'Back to Sales Desk')
        : t('commandCenter.cameras.back');

    const [cameras, setCameras] = useState([]);
    const [canManage, setCanManage] = useState(false);
    const [selected, setSelected] = useState(null);
    const [manageOpen, setManageOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const load = useCallback(() => {
        api.get('/command-center/cameras', { params: org ? { organization_id: org } : {} })
            .then((r) => {
                const list = r.data.cameras || [];
                setCameras(list);
                setCanManage(!!r.data.can_manage);
                setSelected((cur) => cur && list.find((c) => c.id === cur.id) ? list.find((c) => c.id === cur.id) : (list[0] || null));
            })
            .catch(() => setCameras([]))
            .finally(() => setLoaded(true));
    }, [org]);

    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { name: form.name, url: form.url, tenant_visible: form.tenant_visible };
            if (org) payload.organization_id = org;
            if (form.id) {
                await api.put(`/command-center/cameras/${form.id}`, payload);
                toast.success(t('commandCenter.cameras.updated'));
            } else {
                await api.post('/command-center/cameras', payload);
                toast.success(t('commandCenter.cameras.added'));
            }
            setForm(emptyForm);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (cam) => {
        if (!window.confirm(t('commandCenter.cameras.removeConfirm'))) return;
        try {
            await api.delete(`/command-center/cameras/${cam.id}`);
            toast.success(t('commandCenter.cameras.removed'));
            if (form.id === cam.id) setForm(emptyForm);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error');
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-white flex flex-col" data-testid="cameras-page">
            {/* ── Top control bar: Back + camera switch + Manage ── */}
            <div className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-3 border-b border-slate-800 bg-slate-900/60 sticky top-0 z-10">
                <button onClick={() => navigate(backTo)} data-testid="cameras-back"
                    className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white bg-slate-800/70 hover:bg-slate-700 px-3 py-2 rounded-lg transition-colors shrink-0">
                    <ChevronLeft className="w-4 h-4" />{backLabel}
                </button>
                <div className="flex items-center gap-1.5 text-slate-400 px-1 shrink-0">
                    <Video className="w-5 h-5 text-sky-400" />
                    <span className="font-semibold text-white">{t('commandCenter.cameras.title')}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0" data-testid="cameras-switcher">
                    {cameras.map((c) => (
                        <button key={c.id} data-testid={`cameras-switch-${c.id}`}
                            onClick={() => setSelected(c)}
                            className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${selected?.id === c.id ? 'bg-sky-600 text-white' : 'bg-slate-800/70 text-slate-300 hover:bg-slate-700'}`}>
                            {c.name}
                        </button>
                    ))}
                </div>
                {canManage && (
                    <Button variant="outline" size="sm" data-testid="cameras-manage-toggle"
                        onClick={() => setManageOpen((v) => !v)}
                        className="gap-1.5 bg-slate-900/60 border-slate-700 shrink-0">
                        <Settings2 className="w-4 h-4" />{t('commandCenter.cameras.manage')}
                    </Button>
                )}
            </div>

            {/* ── Manage panel (admins only) ── */}
            {canManage && manageOpen && (
                <div className="px-4 sm:px-6 py-4 border-b border-slate-800 bg-slate-900/40" data-testid="cameras-manage-panel">
                    <div className="max-w-2xl space-y-2 mb-4">
                        {cameras.map((c) => (
                            <div key={c.id} className="flex items-center justify-between gap-3 bg-slate-800/50 rounded-lg px-3 py-2" data-testid={`cameras-row-${c.id}`}>
                                <div className="min-w-0">
                                    <p className="text-sm text-white truncate">{c.name}
                                        {c.tenant_visible && <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-400">{t('commandCenter.cameras.tenantVisible')}</span>}
                                    </p>
                                    <p className="text-xs text-slate-500 truncate">{c.url}</p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => { setForm({ id: c.id, name: c.name, url: c.url, tenant_visible: !!c.tenant_visible }); }}
                                        data-testid={`cameras-edit-${c.id}`} className="p-2 text-slate-400 hover:text-white rounded" title={t('commandCenter.cameras.edit')}>
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => remove(c)} data-testid={`cameras-remove-${c.id}`}
                                        className="p-2 text-slate-400 hover:text-red-400 rounded" title={t('commandCenter.cameras.remove')}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <form onSubmit={submit} className="max-w-2xl grid grid-cols-1 sm:grid-cols-[1fr_1.5fr_auto] gap-2 items-end" data-testid="cameras-form">
                        <div>
                            <label className="text-xs text-slate-400">{t('commandCenter.cameras.name')}</label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder={t('commandCenter.cameras.namePlaceholder')} data-testid="cameras-input-name"
                                className="bg-slate-900 border-slate-700" />
                        </div>
                        <div>
                            <label className="text-xs text-slate-400">{t('commandCenter.cameras.url')}</label>
                            <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                                placeholder={t('commandCenter.cameras.urlPlaceholder')} data-testid="cameras-input-url"
                                className="bg-slate-900 border-slate-700" />
                        </div>
                        <div className="flex items-center gap-2">
                            <Button type="submit" disabled={saving} data-testid="cameras-save"
                                className="gap-1.5 bg-emerald-600 hover:bg-emerald-500">
                                {form.id ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                {form.id ? t('commandCenter.cameras.save') : t('commandCenter.cameras.add')}
                            </Button>
                            {form.id && (
                                <Button type="button" variant="ghost" onClick={() => setForm(emptyForm)} data-testid="cameras-cancel-edit"
                                    className="text-slate-400"><X className="w-4 h-4" /></Button>
                            )}
                        </div>
                        <label className="sm:col-span-3 flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid="cameras-tenant-toggle">
                            <input type="checkbox" checked={form.tenant_visible}
                                onChange={(e) => setForm({ ...form, tenant_visible: e.target.checked })}
                                className="w-4 h-4 accent-emerald-500" />
                            {t('commandCenter.cameras.tenantVisible')}
                        </label>
                    </form>
                </div>
            )}

            {/* ── Large viewing area ── */}
            <div className="flex-1 p-4 sm:p-6">
                {!loaded ? null : cameras.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-center" data-testid="cameras-empty">
                        <p className="text-slate-400">{lotScope
                            ? t('commandCenter.cameras.noneLot', 'No lot camera connected yet.')
                            : (canManage ? t('commandCenter.cameras.noneAdmin') : t('commandCenter.cameras.none'))}</p>
                    </div>
                ) : selected ? (
                    <div className="h-full flex flex-col" data-testid="cameras-viewer">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-sm text-slate-300 font-medium">{selected.name}</p>
                            <a href={selected.url} target="_blank" rel="noopener noreferrer" data-testid="cameras-open-newtab"
                                className="flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300">
                                <ExternalLink className="w-3.5 h-3.5" />{t('commandCenter.cameras.openNewTab')}
                            </a>
                        </div>
                        <div className="relative flex-1 min-h-[60vh] rounded-xl overflow-hidden border border-slate-800 bg-black">
                            {isVideoUrl(selected.url) ? (
                                <CameraVideo cam={selected} t={t} />
                            ) : (
                                <iframe key={selected.id} src={selected.url} title={selected.name}
                                    data-testid="cameras-iframe"
                                    className="absolute inset-0 w-full h-full"
                                    allow="fullscreen; autoplay"
                                    referrerPolicy="no-referrer" />
                            )}
                        </div>
                        <p className="text-xs text-slate-500 mt-2">{t('commandCenter.cameras.embedHint')}</p>
                    </div>
                ) : (
                    <div className="h-full flex items-center justify-center">
                        <p className="text-slate-400">{t('commandCenter.cameras.selectCamera')}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
